import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { MemberDatabaseService } from '../shared/services/member-database-public';
import { CreateMemberPayload } from '../shared/types/member-database-public.types';

function maxItems(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return Array.isArray(value) && value.length > max ? { maxItems: { max } } : null;
  };
}

@Component({
  selector: 'fp-member-database-add-new-profile',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    ButtonModule,
    TooltipModule,
  ],
  templateUrl: './member-database-add-new-profile.html',
  styleUrls: ['./member-database-add-new-profile.scss'],
})
export class MemberDatabaseAddNewProfileComponent {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() fieldOptions: { label: string; value: number }[] = [];
  @Input() countryOptions: { label: string; value: number }[] = [];
  @Input() interestOptions: { label: string; value: number }[] = [];

  @Output() profileSubmitted = new EventEmitter<void>();

  private readonly translateService = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly memberDatabaseService = inject(MemberDatabaseService);

  private photoFile: File | null = null;
  photoPreview: string | null = null;
  submitting = false;
  submitted = false;
  errorMessage: string | null = null;
  showSuccessDialog = false;

  readonly form = this.fb.nonNullable.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    linkedin: this.fb.nonNullable.control('', [Validators.required, Validators.pattern(/^https?:\/\/(www\.)?linkedin\.com\/.+/i)]),
    field: this.fb.nonNullable.control<number | null>(null, Validators.required),
    interests: this.fb.nonNullable.control<number[]>([], [Validators.required, maxItems(3)]),
    jobTitle: ['', Validators.required],
    company: ['', Validators.required],
    city: ['', Validators.required],
    country: this.fb.nonNullable.control<number | null>(null, Validators.required),
    consent: [false, Validators.requiredTrue],
  });

  onPhotoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }
    this.photoFile = file;
    const reader = new FileReader();
    reader.onload = () => (this.photoPreview = reader.result as string);
    reader.readAsDataURL(file);
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;
    this.errorMessage = null;

    this.memberDatabaseService
      .createMember(this.toCreatePayload())
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () => this.onCreateSuccess(),
        error: () => {
          this.errorMessage = this.translateService.instant('ADD_PROFILE.CREATE_ERROR');
        },
      });
  }

  onHide(): void {
    this.visibleChange.emit(false);
    this.form.reset();
    this.photoPreview = null;
    this.photoFile = null;
    this.submitted = false;
    this.errorMessage = null;
  }

  private toCreatePayload(): CreateMemberPayload {
    const value = this.form.getRawValue();

    return {
      first_name: value.firstName,
      last_name: value.lastName,
      email: value.email,
      linkedin: value.linkedin,
      position: value.jobTitle,
      company: value.company,
      consent_public_profile: value.consent,
      country: value.country!,
      city_name: value.city,
      field: value.field!,
      interests: value.interests,
      ...(this.photoFile ? { photo: this.photoFile } : {}),
    };
  }

  private onCreateSuccess(): void {
    this.profileSubmitted.emit();
    this.visible = false;
    this.onHide();
    this.showSuccessDialog = true;
  }

  closeConfirmingSuccessDialog(): void {
    this.showSuccessDialog = false;
  }
}
